import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { searchProductImages, selectProductImages } from '../../api/api';
import {useToast} from '../../components/ToastProvider/ToastProvider';

const ImageSearch = ({ 
  stagedProductId, 
  productDescription, 
  productCategory, 
  onImagesSelected,
  onCustomUpload 
}) => {
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { showToast } = useToast();

  const handleSearchImages = async () => {
    setLoading(true);
    
    try {
      const response = await searchProductImages(stagedProductId);
      setImages(response.images || []);
      setHasSearched(true);

      if (!response.images || response.images.length === 0) {
        showToast(
          `No images found for "${productDescription} ${productCategory}". Try uploading a custom image.`,
          'info'
        );
      }
    } catch (err) {
      console.error('Image search error:', err);
      showToast('Failed to search images. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (imageUrl, isSelected) => {
    if (isSelected) {
      setSelectedImages(prev => [...prev, imageUrl]);
    } else {
      setSelectedImages(prev => prev.filter(url => url !== imageUrl));
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedImages.length === 0) {
      showToast('Please select at least one image.', 'warning');
      return;
    }

    try {
      const response = await selectProductImages(stagedProductId, selectedImages);
      onImagesSelected(response.selected_images);
      showToast(`${selectedImages.length} image(s) selected successfully!`, 'success');
    } catch (err) {
      console.error('Image selection error:', err);
      showToast('Failed to save image selection. Please try again.', 'error');
    }
  };

  const handleCustomUpload = () => {
    onCustomUpload();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearchImages}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? <CircularProgress size={20} /> : 'Search Images'}
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={handleCustomUpload}
          sx={{ minWidth: 200 }}
        >
          Upload Custom Image
        </Button>
      </Box>

      {images.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Found {images.length} images - Select the ones you want to use:
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {images.map((image, index) => {
              const isSelected = selectedImages.includes(image.url);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card 
                    sx={{ 
                      position: 'relative',
                      border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleImageSelect(image.url, !isSelected)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={image.url}
                      alt={image.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="body2" noWrap>
                        {image.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {image.source}
                      </Typography>
                    </CardContent>

                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: '#1976d2',
                          borderRadius: '50%',
                          p: 0.5
                        }}
                      >
                        <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {selectedImages.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body2">
                Selected {selectedImages.length} image(s):
              </Typography>
              {selectedImages.map((url, index) => (
                <Chip
                  key={index}
                  label={`Image ${index + 1}`}
                  size="small"
                  onDelete={() => handleImageSelect(url, false)}
                  deleteIcon={<CancelIcon />}
                />
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleConfirmSelection}
            disabled={selectedImages.length === 0}
            sx={{ mb: 2 }}
          >
            Confirm Selection ({selectedImages.length} images)
          </Button>
        </>
      )}
    </Box>
  );
};

export default ImageSearch;
